import React, { useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, IconButton, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import InventoryOutlinedIcon from "@mui/icons-material/InventoryOutlined";
import { useNotifier } from "../../../Core/Notifier";
import { api, T, inputSx } from "./api";

/* ═══════════════════════════════════════════════════════════════════════════
   CATEGORY FORM MODAL
   ═══════════════════════════════════════════════════════════════════════════ */
const CategoryFormModal: React.FC<{
  open: boolean;
  category: any | null;
  serviceId: number;
  onSave: (data: any) => void;
  onClose: () => void;
  saving: boolean;
}> = ({ open, category, serviceId, onSave, onClose, saving }) => {
  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");

  React.useEffect(() => {
    if (category) {
      setName(category.name || "");
      setDescription(category.description || "");
    } else {
      setName("");
      setDescription("");
    }
  }, [category, open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden" } }}>
      <Box sx={{ px: 3, py: 2, background: `linear-gradient(135deg, ${T.navy} 0%, #1a3550 100%)`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: "10px", bgcolor: "rgba(59,130,246,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CategoryOutlinedIcon sx={{ fontSize: 16, color: "#3b82f6" }} />
          </Box>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: T.white }}>
            {category?.id ? "Edit Category" : "New Category"}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: "rgba(255,255,255,0.6)" }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2.5 }}>
        <TextField
          label="Category Name *" size="small" value={name}
          onChange={(e) => setName(e.target.value)} autoFocus fullWidth sx={inputSx}
        />
        <TextField
          label="Description" size="small" value={description}
          onChange={(e) => setDescription(e.target.value)} fullWidth multiline rows={2} sx={inputSx}
        />
      </DialogContent>

      <Box sx={{ px: 3, py: 2, display: "flex", justifyContent: "flex-end", gap: 1.5, borderTop: `1px solid ${T.line}` }}>
        <Button onClick={onClose} sx={{ textTransform: "none", fontSize: 13, color: T.slate }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => onSave({ id: category?.id, service_id: serviceId, name: name.trim(), description: description || null })}
          disabled={saving || !name.trim()}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveAltIcon sx={{ fontSize: 16 }} />}
          sx={{ textTransform: "none", fontSize: 13, fontWeight: 600, bgcolor: T.navy, borderRadius: "10px", px: 3, "&:hover": { bgcolor: "#1a3550" } }}
        >
          {saving ? "Saving..." : "Save Category"}
        </Button>
      </Box>
    </Dialog>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   CATEGORIES SECTION
   ═══════════════════════════════════════════════════════════════════════════ */
const CategoriesSection: React.FC<{
  serviceId: number;
  categories: any[];
  serviceColor: string;
  onRefresh: () => void;
}> = ({ serviceId, categories, serviceColor, onRefresh }) => {
  const { showMessage, showErrorMessage } = useNotifier();
  const [formOpen, setFormOpen] = useState(false);
  const [editCat, setEditCat] = useState<any | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; name: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleSave = async (data: any) => {
    setSaving(true);
    const res = await api.post("/banquet/service-tree/category", data);
    if (res.code === 200) {
      showMessage(data.id ? "Category updated" : "Category created");
      setFormOpen(false);
      setEditCat(null);
      onRefresh();
    } else showErrorMessage(res.message || "Failed to save");
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    const res = await api.del(`/banquet/service-tree/category/${deleteTarget.id}`);
    if (res.code === 200) { showMessage("Category deleted"); onRefresh(); }
    else showErrorMessage(res.message);
    setIsDeleting(false);
    setDeleteTarget(null);
  };

  const thSx = { fontWeight: 700, fontSize: 11, color: T.slate, textTransform: "uppercase", letterSpacing: "0.05em", py: 1.5 } as const;

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CategoryOutlinedIcon sx={{ fontSize: 18, color: "#3b82f6" }} />
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: T.navy }}>Categories</Typography>
          <Chip label={categories.length} size="small" sx={{ height: 20, fontSize: 11, fontWeight: 600, bgcolor: "#3b82f612", color: "#3b82f6" }} />
        </Box>
        <Button size="small" startIcon={<AddIcon sx={{ fontSize: 14 }} />}
          onClick={() => { setEditCat(null); setFormOpen(true); }}
          sx={{ textTransform: "none", fontSize: 12, fontWeight: 600, color: T.navy, bgcolor: T.bg, borderRadius: "8px", px: 1.5, "&:hover": { bgcolor: T.line } }}>
          Add Category
        </Button>
      </Box>

      {categories.length === 0 ? (
        <Paper elevation={0} sx={{ p: 3, textAlign: "center", borderRadius: "12px", border: `1px dashed ${T.line}` }}>
          <CategoryOutlinedIcon sx={{ fontSize: 32, color: T.slateLt, mb: 0.5 }} />
          <Typography sx={{ fontSize: 12, color: T.slateLt }}>No categories yet. Add one to organize items.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: "12px", border: `1px solid ${T.line}` }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: T.bg }}>
                <TableCell sx={{ ...thSx, width: 40 }}>#</TableCell>
                <TableCell sx={thSx}>Category</TableCell>
                <TableCell align="center" sx={thSx}>Subcategories</TableCell>
                <TableCell align="center" sx={thSx}>Items</TableCell>
                <TableCell align="right" sx={thSx}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {categories.map((cat: any, idx: number) => {
                const subCount = (cat.subcategories ?? []).length;
                const directItems = (cat.items ?? []).length;
                const subItems = (cat.subcategories ?? []).reduce((t: number, s: any) => t + (s.items ?? []).length, 0);
                const totalItems = directItems + subItems;

                return (
                  <TableRow key={cat.id} hover sx={{ "&:hover": { bgcolor: "#fef6ee" } }}>
                    <TableCell sx={{ py: 1.2, fontSize: 12, color: T.slateLt }}>{idx + 1}</TableCell>
                    <TableCell sx={{ py: 1.2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: serviceColor, flexShrink: 0 }} />
                        <Box>
                          <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.navy }}>{cat.name}</Typography>
                          {cat.description && <Typography sx={{ fontSize: 11, color: T.slateLt, mt: 0.2 }} noWrap>{cat.description}</Typography>}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1.2 }}>
                      <Chip label={subCount} size="small" sx={{ height: 20, fontSize: 11, fontWeight: 600, bgcolor: "#8b5cf612", color: "#8b5cf6" }} />
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1.2 }}>
                      <Chip label={totalItems} size="small" sx={{ height: 20, fontSize: 11, fontWeight: 600, bgcolor: T.greenBg, color: T.greenTx }} />
                    </TableCell>
                    <TableCell align="right" sx={{ py: 1.2 }}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => { setEditCat(cat); setFormOpen(true); }} sx={{ color: T.slateLt, "&:hover": { color: T.gold } }}>
                          <EditOutlinedIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => setDeleteTarget({ id: cat.id, name: cat.name })} sx={{ color: T.slateLt, "&:hover": { color: T.redTx } }}>
                          <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <CategoryFormModal open={formOpen} category={editCat} serviceId={serviceId} onSave={handleSave}
        onClose={() => { setFormOpen(false); setEditCat(null); }} saving={saving} />

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" PaperProps={{ sx: { borderRadius: "14px" } }}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 16 }}>
          <WarningAmberIcon color="error" /> Delete category?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 13 }}>
            Delete <strong>{deleteTarget?.name}</strong> and all its subcategories and items?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} sx={{ textTransform: "none", color: T.slate }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={isDeleting}
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px" }}>{isDeleting ? "..." : "Delete"}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CategoriesSection;
